import { nameof } from "ts-simple-nameof";
import { useServiceSelection } from "../hooks/useServiceSelection";
import { useAppContext } from "../../../../../AppContext";
import { HiddenField } from "../../../../../common/components/forms/inputs/hiddenField/HiddenField";
import { DefaultFormSelectField } from "../../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { RenderWhen } from "../../../../../common/infrastructure/components/RenderWhen";
import { IEventWizardInitialData } from "../../models/eventWizardInitialDataModel";
import { useFormContext } from "react-hook-form";
import { IServiceWizardFormModel } from "../models/serviceWizardFormModel";
import { PageMode } from "../../../../../common/types";
import { IEmployeeWithAvailabilityModel } from "../../../../../common/components/forms/inputs/instructorCalendar/models/employeeCalendarModels";
import { EmployeeCalendar } from "../../../../../common/components/forms/inputs/instructorCalendar/EmployeeCalendar";
import { TimeDurationField } from "../../../../../common/components/forms/inputs/timeDurationField/TimeDurationField";
import { DefaultToggle } from "../../../../../common/components/forms/inputs/toggle/DefaultToggle";
import { MessageBanner } from "../../../../../common/components/common/MessageBanner/MessageBanner";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";

interface IServiceWizardMainInputsProps {
  enableMultisport?: boolean;
  enableCoolFit?: boolean;
  studioEmployees?: IEmployeeWithAvailabilityModel[];
  /** The selected service's buffer after the reservation. */
  bufferAfterMinutes?: number;
  initialData?: IEventWizardInitialData;
  /** The clients to pick from; defaults to the loaded list. */
  userOptions?: ISelectFieldModel[];
  pageMode?: PageMode;
  isLoading: boolean;
}

export const ServiceWizardMainInputs = ({
  enableMultisport,
  enableCoolFit,
  studioEmployees,
  bufferAfterMinutes,
  initialData,
  userOptions,
  pageMode,
  isLoading,
}: IServiceWizardMainInputsProps) => {
  const form = useFormContext<IServiceWizardFormModel>();
  const { currentUser } = useAppContext();

  const serviceId = form.watch("serviceId");
  const { getFilteredEmployees } = useServiceSelection(
    initialData?.services,
    serviceId,
    studioEmployees,
    currentUser,
    pageMode,
    initialData,
  );

  const filteredEmployees = getFilteredEmployees(serviceId);

  const filteredEmployeesForCalendar =
    studioEmployees?.filter((employee) =>
      filteredEmployees.some((filtered) => filtered.id === employee.id),
    ) || [];

  const forceEmailLabel = "Уведоми клиентът при записване за услугата";

  // Only surface the "no services" banner when adding. In edit mode the reservation
  // already has a service selected, so the form must render even if the (restricted)
  // employee is currently assigned to no service types.
  const hasNoServices =
    pageMode !== "edit" &&
    !isLoading &&
    (initialData?.services?.length ?? 0) === 0;

  if (hasNoServices) {
    return (
      <>
        <MessageBanner
          type="info"
          message="Към момента няма активни услуги, за които да можете да създадете резервация. Моля добавете такива от меню 'Типове услуги' или се свържете с мениджър на обекта."
        />
        {/* Breathing room below the message when nothing else is rendered */}
        <div style={{ height: "40px" }} />
      </>
    );
  }

  return (
    <>
      <HiddenField name={nameof<IServiceWizardFormModel>((x) => x.studioId)} />
      <HiddenField name={nameof<IServiceWizardFormModel>((x) => x.price)} />

      <DefaultFormSelectField
        name={nameof<IServiceWizardFormModel>((x) => x.serviceId)}
        label="Услуга"
        options={initialData?.services}
        required
        tooltip="Изберете услугата, която ще бъде предоставена на клиента."
        disabled={isLoading}
      />

      <TimeDurationField
        name={nameof<IServiceWizardFormModel>((x) => x.durationInMinutes)}
        label="Продължителност"
        required
        tooltip="Продължителността се попълва автоматично според избраната услуга, но може да бъде променена при нужда."
      />

      <EmployeeCalendar
        employeeFieldName={nameof<IServiceWizardFormModel>((x) => x.employeeId)}
        dateFieldName={nameof<IServiceWizardFormModel>((x) => x.date)}
        durationFieldName={nameof<IServiceWizardFormModel>(
          (x) => x.durationInMinutes,
        )}
        bufferAfterMinutes={bufferAfterMinutes}
        employeesData={filteredEmployeesForCalendar}
        label="Дата и час на започване"
        required
        disabled={isLoading}
      />

      <DefaultFormSelectField
        name={nameof<IServiceWizardFormModel>((x) => x.userId)}
        label="Клиент"
        options={userOptions ?? initialData?.allAvailableUsers}
        tooltip="Клиентът, който ще получи услугата."
        disabled={isLoading}
        required
        filter
      />

      <RenderWhen condition={!!enableMultisport}>
        <DefaultToggle
          name={nameof<IServiceWizardFormModel>((x) => x.isMultisportAvailable)}
          label="Разрешен ли е мултиспорт?"
        />
      </RenderWhen>

      <RenderWhen condition={!!enableCoolFit}>
        <DefaultToggle
          name={nameof<IServiceWizardFormModel>((x) => x.isCoolFitAvailable)}
          label="Разрешен ли е CoolFit?"
        />
      </RenderWhen>

      <RenderWhen condition={pageMode === "add"}>
        <DefaultToggle
          name={nameof<IServiceWizardFormModel>(
            (x) => x.forceEmailNotifications,
          )}
          label={forceEmailLabel}
          tooltip={
            "Тази настройка ще изпрати имейл уведомление до клиента, независимо дали е дал съгласие да получава съобщения. Използвай я само когато е наложително."
          }
        />
      </RenderWhen>
    </>
  );
};
